import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { flowFetch, FlowArea, FlowBoard, FlowColumn, FlowUser, formatDuration, formatDate } from './flowApi';
import { FlowTaskList } from './FlowTasks';
import { FlowDocumentList } from './FlowDocuments';

const AREA_COLORS = ['#0ea5e9', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#10b981', '#eab308', '#64748b'];

interface AreaFormModalProps {
  area?: FlowArea;
  users: FlowUser[];
  onClose: () => void;
  onSaved: () => void;
}

function AreaFormModal({ area, users, onClose, onSaved }: AreaFormModalProps) {
  const [name, setName] = useState(area?.name || '');
  const [description, setDescription] = useState(area?.description || '');
  const [color, setColor] = useState(area?.color || AREA_COLORS[0]);
  const [memberIds, setMemberIds] = useState<string[]>(area?.members?.map(m => m.userId) || []);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Informe o nome da área'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ name, description, color, memberIds });
      if (area) await flowFetch(`/api/flow/areas/${area.id}`, { method: 'PUT', body });
      else await flowFetch('/api/flow/areas', { method: 'POST', body });
      toast.success('Área salva!');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar área');
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async () => {
    if (!area) return;
    try {
      await flowFetch(`/api/flow/areas/${area.id}`, { method: 'PUT', body: JSON.stringify({ archived: !area.archived }) });
      toast.success(area.archived ? 'Área restaurada' : 'Área arquivada');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro');
    }
  };

  const handleDelete = async () => {
    if (!area) return;
    if (!confirm(`Excluir a área "${area.name}"?\n\nIsto apaga TODOS os quadros, tarefas, documentos, comentários e conversas desta área. Esta ação não pode ser desfeita.`)) return;
    try {
      await flowFetch(`/api/flow/areas/${area.id}`, { method: 'DELETE' });
      toast.success('Área excluída');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao excluir área');
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="mb-4 text-lg font-bold text-slate-900">{area ? 'Editar área' : 'Nova área'}</h2>
        <div className="space-y-3">
          <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Ex.: Operacional"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <textarea value={description || ''} onChange={e => setDescription(e.target.value)} rows={2}
            placeholder="Descrição — ex.: Pedidos, produção, despacho e rotina do dia a dia."
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <div className="flex gap-2">
            {AREA_COLORS.map(c => (
              <button key={c} onClick={() => setColor(c)}
                className={`h-7 w-7 rounded-full ${color === c ? 'ring-2 ring-offset-2 ring-slate-500' : ''}`} style={{ background: c }} />
            ))}
          </div>
          <div>
            <p className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">Quem tem acesso a esta área</p>
            <div className="grid max-h-44 grid-cols-2 gap-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {users.map(u => (
                <label key={u.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="accent-cyan-600" checked={memberIds.includes(u.id)}
                    onChange={e => setMemberIds(e.target.checked ? [...memberIds, u.id] : memberIds.filter(id => id !== u.id))} />
                  {u.nome}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between">
          {area ? (
            <div className="flex gap-3">
              <button onClick={toggleArchive} className="text-xs font-semibold text-amber-600 hover:text-amber-700">
                {area.archived ? 'Restaurar área' : 'Arquivar área'}
              </button>
              <button onClick={handleDelete} className="text-xs font-semibold text-red-600 hover:text-red-700">
                Excluir área
              </button>
            </div>
          ) : <span />}
          <div className="flex gap-3">
            <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700">Cancelar</button>
            <button onClick={handleSave} disabled={saving}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50">
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type AdminTab = 'areas' | 'tarefas' | 'documentos' | 'diarias';

export function FlowAdmin({ areas, users, onAreasChanged }: { areas: FlowArea[]; users: FlowUser[]; onAreasChanged: () => void }) {
  const [tab, setTab] = useState<AdminTab>('areas');
  const [editing, setEditing] = useState<{ area?: FlowArea } | null>(null);
  const [managingBoards, setManagingBoards] = useState<FlowArea | null>(null);

  const tabBtn = (t: AdminTab, label: string) => (
    <button key={t} onClick={() => setTab(t)}
      className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${tab === t ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}>
      {label}
    </button>
  );

  return (
    <div>
      <div className="mb-4 inline-flex gap-1 rounded-2xl bg-slate-100 p-1">
        {tabBtn('areas', 'Áreas e permissões')}
        {tabBtn('tarefas', 'Todas as tarefas')}
        {tabBtn('documentos', 'Todos os documentos')}
        {tabBtn('diarias', 'Tarefas diárias')}
      </div>

      {tab === 'areas' && (
        <div>
          <div className="mb-4 flex justify-end">
            <button onClick={() => setEditing({})}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cyan-700">
              + Nova área
            </button>
          </div>
          {areas.length === 0 ? (
            <p className="py-12 text-center text-slate-400">Nenhuma área criada. Crie a primeira — ex.: Gerência, Operacional, Expedição...</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Área</th>
                    <th className="px-4 py-3">Descrição</th>
                    <th className="px-4 py-3">Acesso</th>
                    <th className="px-4 py-3">Tarefas abertas</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {areas.map(a => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2 font-semibold text-slate-900">
                          <span className="h-3 w-3 rounded-full" style={{ background: a.color }} />
                          {a.name}
                          {a.archived && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-bold text-amber-700">ARQUIVADA</span>}
                        </span>
                      </td>
                      <td className="max-w-xs truncate px-4 py-3 text-slate-500">{a.description || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{(a.members || []).length} usuários</td>
                      <td className="px-4 py-3 text-slate-600">{a.openTasks ?? 0}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setManagingBoards(a)} className="mr-3 text-slate-600 hover:text-slate-900">Quadros</button>
                        <button onClick={() => setEditing({ area: a })} className="text-cyan-600 hover:text-cyan-800">Editar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'tarefas' && <FlowTaskList areas={areas} users={users} />}
      {tab === 'documentos' && <FlowDocumentList areas={areas} users={users} />}
      {tab === 'diarias' && <DailyTasksPanel />}

      {editing && (
        <AreaFormModal area={editing.area} users={users} onClose={() => setEditing(null)} onSaved={onAreasChanged} />
      )}
      {managingBoards && (
        <BoardsManagerModal area={managingBoards} onClose={() => setManagingBoards(null)} onChanged={onAreasChanged} />
      )}
    </div>
  );
}

// ---------- Gerenciador de Quadros e Colunas (admin) ----------
function BoardsManagerModal({ area, onClose, onChanged }: { area: FlowArea; onClose: () => void; onChanged: () => void }) {
  const [boards, setBoards] = useState<FlowBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [newBoard, setNewBoard] = useState('');
  const [openBoard, setOpenBoard] = useState<string | null>(null);
  const [columns, setColumns] = useState<Record<string, FlowColumn[]>>({});
  const [newColName, setNewColName] = useState('');
  const [newColKind, setNewColKind] = useState('TODO');

  const loadBoards = async () => {
    setLoading(true);
    try {
      // archived=true: inclui arquivados para permitir restaurar/excluir
      const data = await flowFetch(`/api/flow/boards?areaId=${area.id}&archived=true`);
      setBoards(data.boards);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar quadros');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadBoards(); }, [area.id]);

  const loadColumns = async (boardId: string) => {
    try {
      const data = await flowFetch(`/api/flow/boards/${boardId}`);
      setColumns(prev => ({ ...prev, [boardId]: data.columns }));
    } catch { /* ignore */ }
  };

  const toggleBoard = (id: string) => {
    if (openBoard === id) { setOpenBoard(null); return; }
    setOpenBoard(id);
    if (!columns[id]) loadColumns(id);
  };

  const createBoard = async () => {
    if (!newBoard.trim()) return;
    try {
      await flowFetch('/api/flow/boards', { method: 'POST', body: JSON.stringify({ areaId: area.id, name: newBoard.trim() }) });
      setNewBoard('');
      loadBoards();
      onChanged();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao criar quadro'); }
  };

  const renameBoard = async (id: string, name: string) => {
    try { await flowFetch(`/api/flow/boards/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }); loadBoards(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro'); }
  };
  const archiveBoard = async (id: string) => {
    if (!confirm('Arquivar este quadro?')) return;
    try { await flowFetch(`/api/flow/boards/${id}`, { method: 'PUT', body: JSON.stringify({ archived: true }) }); loadBoards(); onChanged(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro'); }
  };
  const restoreBoard = async (id: string) => {
    try { await flowFetch(`/api/flow/boards/${id}`, { method: 'PUT', body: JSON.stringify({ archived: false }) }); toast.success('Quadro restaurado'); loadBoards(); onChanged(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro'); }
  };
  const deleteBoard = async (b: FlowBoard) => {
    if (!confirm(`EXCLUIR o quadro "${b.name}" INTEIRO?\n\nTodas as tarefas, checklists, comentários e anexos deste quadro serão apagados permanentemente. Esta ação não pode ser desfeita.`)) return;
    try {
      const data = await flowFetch(`/api/flow/boards/${b.id}`, { method: 'DELETE' });
      toast.success(`Quadro excluído${data.deletedTasks ? ` (${data.deletedTasks} tarefa(s) removida(s))` : ''}`);
      loadBoards(); onChanged();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao excluir quadro'); }
  };

  const addColumn = async (boardId: string) => {
    if (!newColName.trim()) return;
    try {
      await flowFetch('/api/flow/columns', { method: 'POST', body: JSON.stringify({ boardId, name: newColName.trim(), kind: newColKind }) });
      setNewColName(''); setNewColKind('TODO');
      loadColumns(boardId);
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao criar coluna'); }
  };
  const archiveColumn = async (boardId: string, colId: string) => {
    try { await flowFetch(`/api/flow/columns/${colId}`, { method: 'PUT', body: JSON.stringify({ archived: true }) }); loadColumns(boardId); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Mova as tarefas antes de arquivar'); }
  };

  const KIND_LABEL: Record<string, string> = { TODO: 'A fazer', DOING: 'Fazendo', WAITING: 'Aguardando', DONE: 'Concluído' };

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-900">Quadros de {area.name}</h2>
          <p className="text-sm text-slate-500">Crie quadros e configure as colunas/status de cada um.</p>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 flex gap-2">
            <input value={newBoard} onChange={e => setNewBoard(e.target.value)} onKeyDown={e => e.key === 'Enter' && createBoard()}
              placeholder="Nome do novo quadro — ex.: Conteúdos Instagram" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <button onClick={createBoard} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700">+ Quadro</button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>
          ) : boards.length === 0 ? (
            <p className="py-8 text-center text-slate-400">Nenhum quadro. Crie o primeiro acima.</p>
          ) : (
            <div className="space-y-2">
              {boards.map(b => (
                <div key={b.id} className={`rounded-xl border ${b.archived ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200'}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                    <button onClick={() => toggleBoard(b.id)} className="flex min-w-0 items-center gap-2 text-sm font-bold text-slate-800">
                      <svg className={`h-4 w-4 shrink-0 transition ${openBoard === b.id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      <span className="truncate">{b.name}</span>
                      {b.archived && <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Arquivado</span>}
                    </button>
                    <div className="flex items-center gap-3 text-xs">
                      {b.archived ? (
                        <button onClick={() => restoreBoard(b.id)} className="text-emerald-600 hover:text-emerald-800">Restaurar</button>
                      ) : (
                        <>
                          <button onClick={() => { const n = prompt('Novo nome do quadro', b.name); if (n?.trim()) renameBoard(b.id, n.trim()); }}
                            className="text-cyan-600 hover:text-cyan-800">Renomear</button>
                          <button onClick={() => archiveBoard(b.id)} className="text-amber-600 hover:text-amber-800">Arquivar</button>
                        </>
                      )}
                      <button onClick={() => deleteBoard(b)} className="font-semibold text-red-500 hover:text-red-700">Excluir</button>
                    </div>
                  </div>
                  {openBoard === b.id && (
                    <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
                      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Colunas / Status</p>
                      <div className="mb-3 flex flex-wrap gap-2">
                        {(columns[b.id] || []).map(c => (
                          <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
                            {c.name}
                            <span className="text-[10px] font-normal text-slate-400">({KIND_LABEL[c.kind]})</span>
                            <button onClick={() => archiveColumn(b.id, c.id)} className="text-slate-300 hover:text-red-500">×</button>
                          </span>
                        ))}
                        {(columns[b.id] || []).length === 0 && <span className="text-xs text-slate-400">Carregando...</span>}
                      </div>
                      <div className="flex gap-2">
                        <input value={openBoard === b.id ? newColName : ''} onChange={e => setNewColName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addColumn(b.id)}
                          placeholder="Nova coluna" className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                        <select value={newColKind} onChange={e => setNewColKind(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                          {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <button onClick={() => addColumn(b.id)} className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-800">+</button>
                      </div>
                      <p className="mt-2 text-[11px] text-slate-400">O "comportamento" define como a coluna conta nos dashboards (ex.: Concluído marca a tarefa como finalizada).</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end border-t border-slate-200 px-6 py-4">
          <button onClick={onClose} className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200">Fechar</button>
        </div>
      </div>
    </div>
  );
}

// Painel de tarefas diárias + histórico (admin)
function DailyTasksPanel() {
  const [tasks, setTasks] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        flowFetch('/api/flow/daily-tasks'),
        flowFetch('/api/flow/daily-logs')
      ]);
      setTasks(t.tasks || []);
      setLogs(l.logs || []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar tarefas diárias');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="flex justify-center py-10"><div className="h-6 w-6 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-sm font-bold text-slate-700">Tarefas diárias ativas</h3>
        {tasks.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Nenhuma tarefa diária configurada.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Tarefa</th>
                  <th className="px-4 py-3">Área</th>
                  <th className="px-4 py-3">Responsável</th>
                  <th className="px-4 py-3">Reinicia às</th>
                  <th className="px-4 py-3">Status hoje</th>
                  <th className="px-4 py-3">Tempo hoje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tasks.map(t => (
                  <tr key={t.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">{t.title}</td>
                    <td className="px-4 py-3 text-slate-600">{t.area?.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{t.assignee?.nome || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{t.resetTime || '00:00'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${t.status === 'DONE' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {t.status === 'DONE' ? 'Concluída' : 'Em aberto'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDuration(t.timeSpentSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-bold text-slate-700">Histórico das diárias</h3>
        {logs.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Ainda não há histórico. Os registros aparecem após cada reinício diário.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Dia</th>
                  <th className="px-4 py-3">Tarefa</th>
                  <th className="px-4 py-3">Área</th>
                  <th className="px-4 py-3">Responsável</th>
                  <th className="px-4 py-3">Concluída?</th>
                  <th className="px-4 py-3">Tempo gasto</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map(l => (
                  <tr key={l.id}>
                    <td className="px-4 py-3 text-slate-600">{formatDate(l.refDate)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{l.title}</td>
                    <td className="px-4 py-3 text-slate-600">{l.area?.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{l.assignee?.nome || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${l.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {l.completed ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDuration(l.timeSpentSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
